import { Injectable, signal, WritableSignal } from '@angular/core';

import { AppointmentOptionType } from '../components/calendar/appointment-option.interface';
import { PriceDisplay } from '../components/dynamic-form/price-display';
import { Establishment } from '../components/establishment-card/establishment.interface';
import { ReplacementTransportSelection } from '../components/replacement-transport/replacement-transport.interface';

export interface AppointmentDetailsData {
  salutation: string | null;
  firstName: string;
  lastName: string;
  zipcode: string;
  houseNumber: string;
  houseNumberSuffix: string;
  street: string;
  city: string;
  phoneNumber: string;
  email: string;
  // Brand-newsletter opt-in (checkbox under the email field); default false.
  newsletterOptIn: boolean;
}

// Price as PriceDisplay (not a preformatted string), so the overview shows the same
// from- and promo prices as the cards in the form.
export interface ServiceChoice {
  label: string;
  price?: PriceDisplay;
}

export type ServiceSubEntry =
  | { kind: 'choice'; label: string; price?: PriceDisplay }
  | { kind: 'remark'; question: string; answer: string; price?: PriceDisplay };

export interface ServiceGroup {
  top: ServiceChoice | null;
  subs: ServiceSubEntry[];
}

export interface SelectedAppointment {
  dateIso: string;
  time: string;
  // How the appointment takes place: wait / drop off / collect & deliver.
  appointmentOption: AppointmentOptionType | null;
  replacementTransport: ReplacementTransportSelection;
}

@Injectable({ providedIn: 'root' })
export class BookingDataService {
  public readonly serviceGroups: WritableSignal<ServiceGroup[] | null> = signal<
    ServiceGroup[] | null
  >(null);
  public readonly selectedEstablishment: WritableSignal<Establishment | null> =
    signal<Establishment | null>(null);
  public readonly selectedAppointment: WritableSignal<SelectedAppointment | null> =
    signal<SelectedAppointment | null>(null);
  public readonly appointmentDetails: WritableSignal<AppointmentDetailsData | null> =
    signal<AppointmentDetailsData | null>(null);
  // Free-text remark for the dealer (step 5); only travels along in the confirm POST.
  public readonly remarks: WritableSignal<string> = signal<string>('');

  public setServiceGroups(groups: ServiceGroup[]): void {
    this.serviceGroups.set(groups);
  }

  public setSelectedEstablishment(establishment: Establishment): void {
    this.selectedEstablishment.set(establishment);
  }

  // null = there is no complete appointment (yet). This keeps step 3's snapshot in sync with the
  // on-screen choices; without it a revisit reads back the last confirmed appointment.
  public setSelectedAppointment(appointment: SelectedAppointment | null): void {
    this.selectedAppointment.set(appointment);
  }

  public setAppointmentDetails(data: AppointmentDetailsData): void {
    this.appointmentDetails.set(data);
  }

  public clear(): void {
    this.serviceGroups.set(null);
    this.selectedEstablishment.set(null);
    this.selectedAppointment.set(null);
    this.appointmentDetails.set(null);
    this.remarks.set('');
  }

  public clearFromStep(stepIndex: number): void {
    if (stepIndex <= 0) {
      this.serviceGroups.set(null);
    }
    if (stepIndex <= 1) {
      this.selectedEstablishment.set(null);
    }
    if (stepIndex <= 2) {
      this.selectedAppointment.set(null);
    }
    if (stepIndex <= 3) {
      this.appointmentDetails.set(null);
    }
  }
}
